/**
 * Simple timing profiler for window-exposed methods.
 * Logs execution time to console after each method call.
 */

let enabled = true;

/**
 * Wrapper for synchronous functions.
 */
export const profileSync = <T extends (...args: any[]) => any>(name: string, fn: T): T => {
  return ((...args: Parameters<T>): ReturnType<T> => {
    if (!enabled) {
      return fn(...args);
    }
    const startTime = performance.now();
    const result = fn(...args);
    const duration = performance.now() - startTime;
    console.log(`[Profiler] ${name} completed in ${duration.toFixed(2)}ms`);
    return result;
  }) as T;
};

/**
 * Wrapper for asynchronous functions.
 */
export const profileAsync = <T extends (...args: any[]) => Promise<any>>(name: string, fn: T): T => {
  return (async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    if (!enabled) {
      return fn(...args);
    }
    const startTime = performance.now();
    const result = await fn(...args);
    const duration = performance.now() - startTime;
    console.log(`[Profiler] ${name} completed in ${duration.toFixed(2)}ms`);
    return result;
  }) as T;
};

/**
 * Enable or disable the profiler.
 */
export const setProfilerEnabled = (value: boolean): void => {
  enabled = value;
  console.log(`[Profiler] ${enabled ? 'Enabled' : 'Disabled'}`);
};

/**
 * Check if profiler is enabled.
 */
export const isProfilerEnabled = (): boolean => enabled;
